import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Collapse, Grow, Typography } from "@mui/material";
import { Check, ChevronDown, GripVertical, ListChecks, MinusIcon, Plus, Trash2 } from "lucide-react";
import AppButton from "../../../common_components/AppButton";
import { SearchInput } from "../../../common_components/FormInputs";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import BreakdownDimensionPicker from "./BreakdownDimensionPicker";
import CustomMetricDialog from "./CustomMetricDialog";
import { toApiColumnKey } from "../reportPayload";
import { getNetwork } from "../reportBuilder.networks";
import NetworkFilters from "./NetworkFilters";
import { GroupIcon } from "./builderIcons";
/**
 * Whether a metric can be a column right now: its required dimensions are all
 * picked (Account ID needs Campaign), and no ticked Additional Breakdown drops
 * it (Ad Name drops Account ID). Breakdowns only count under Campaign.
 */
export const isMetricAvailable = (metric, network, dimensions, breakdowns, source = null) => {
    if (source?.unsupportedMetrics.includes(metric.key))
        return false;
    if (!metric.requiresDimensions.every((d) => dimensions.includes(d)))
        return false;
    if (!dimensions.includes("campaign"))
        return true;
    return !network.breakdownFields.some((field) => breakdowns.includes(field.key) && field.unsupportedMetrics.includes(metric.key));
};
/** The network's picked source (Email: Campaign / Flow), falling back to its default. */
export const sourceFor = (network, picked) => {
    if (network.sourceChoices.length === 0)
        return null;
    const key = picked || network.defaultSource || network.sourceChoices[0].key;
    return network.sourceChoices.find((choice) => choice.key === key) || network.sourceChoices[0];
};
/** Column id for a network's metric — "meta.spend", like a combined formula's `{meta.spend}`. */
export const metricId = (networkKey, key) => `${networkKey}.${key}`;
/** Brand colour behind each network's icon while it is switched on. */
const networkColor = (key) => getNetwork(key)?.color ?? "#667085";
/** Column list caps here and scrolls inside — roughly eight rows. */
const LIST_MAX_HEIGHT = 280;
/** Width of the rail, and of the Selected Metrics pane that opens beside it. */
const RAIL_WIDTH = 344;
/** Selected Metrics fills the pane's height before it scrolls. */
const SELECTED_LIST_MAX_HEIGHT = "calc(100vh - 260px)";
/** How close to an edge a drag has to get before the list scrolls. */
const AUTO_SCROLL_EDGE_PX = 28;
const AUTO_SCROLL_STEP_PX = 10;
/** Move one item within a list, returning a new array. */
const moveItem = (ids, from, to) => {
    if (from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length)
        return ids;
    const next = [...ids];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
};
const Section = ({ label, caption, locked, lockedHint, expanded, onToggle, onAdd, addRef, children, }) => {
    const open = expanded && !locked && Boolean(children);
    return (_jsxs(Box, { sx: {
            bgcolor: "#fff",
            border: `1px solid ${open ? "#D0D5DD" : "#E4E7EC"}`,
            borderRadius: "8px",
            opacity: locked ? 0.55 : 1,
            overflow: "hidden",
            boxShadow: open ? "0 1px 3px rgba(16, 24, 40, 0.06)" : "none",
            transition: "border-color .2s ease, box-shadow .2s ease, opacity .2s ease",
        }, children: [_jsxs(Box, { role: "button", tabIndex: locked ? -1 : 0, "aria-expanded": open, "aria-disabled": locked, onClick: () => !locked && onToggle(), onKeyDown: (e) => {
                    if (!locked && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        onToggle();
                    }
                }, sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    cursor: locked ? "not-allowed" : "pointer",
                    bgcolor: open ? "#FCFCFD" : "transparent",
                    transition: "background-color .2s ease",
                    "&:hover": locked ? undefined : { bgcolor: "#F9FAFB" },
                    "&:focus-visible": { outline: "none", boxShadow: "inset 0 0 0 2px rgba(255,0,142,0.25)" },
                }, children: [_jsx(Typography, { className: "all-Font-Sizes-16", sx: { flex: 1, fontWeight: 600, color: "#101828" }, children: label }), caption && (_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#98A2B3" }, children: caption })), onAdd ? (_jsx(HoverTooltip, { title: locked ? lockedHint || "" : `Add ${label.toLowerCase()}`, placement: "top", children: _jsx(Box, { ref: addRef, role: "button", "aria-label": `Add ${label.toLowerCase()}`, tabIndex: locked ? -1 : 0, onClick: (e) => {
                                e.stopPropagation();
                                if (!locked)
                                    onAdd();
                            }, onKeyDown: (e) => {
                                if (!locked && (e.key === "Enter" || e.key === " ")) {
                                    e.stopPropagation();
                                    onAdd();
                                }
                            }, sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 26,
                                height: 26,
                                borderRadius: "6px",
                                color: "#475467",
                                cursor: locked ? "not-allowed" : "pointer",
                                "&:hover": locked ? undefined : { bgcolor: "#F2F4F7", color: "#101828" },
                            }, children: _jsx(Plus, { size: 16 }) }) })) : (_jsx(Box, { sx: {
                            display: "flex",
                            color: open ? "#344054" : "#98A2B3",
                            transform: open ? "rotate(180deg)" : "none",
                            transition: "transform .25s ease, color .2s ease",
                        }, children: _jsx(ChevronDown, { size: 16 }) }))] }), _jsx(Collapse, { in: open, timeout: 250, unmountOnExit: true, children: _jsx(Box, { sx: { px: 1.5, pb: 1.5, borderTop: "1px solid #F2F4F7", pt: 1.25 }, children: children }) })] }));
};
/**
 * One column in the Selected Metrics pane.
 *
 * The list IS the column order, so rows reorder by drag and drop, using native
 * HTML5 drag events (the project carries no DnD dependency). While a row is
 * dragged the list reorders live under it and the other rows glide into their
 * new places (see `useFlip`); the order is committed on drop. Alt+Arrow moves
 * a row from the keyboard, Delete removes it.
 */
const SelectedRow = ({ index, label, networkKey, disabled = false, onRemove, isDragging = false, onDragStart, onDragOverRow, onDropRow, onDragEnd, onNudge, rowRef, }) => {
    const draggable = !disabled;
    return (_jsxs(Box, { ref: rowRef, draggable: draggable, onDragStart: onDragStart, onDragOver: (e) => {
            if (!draggable)
                return;
            e.preventDefault();
            onDragOverRow?.(e);
        }, onDrop: (e) => {
            if (!draggable)
                return;
            e.preventDefault();
            onDropRow?.();
        }, onDragEnd: onDragEnd, tabIndex: draggable ? 0 : -1, "aria-label": `${index + 1}. ${label}`, onKeyDown: (e) => {
            if (!draggable)
                return;
            if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
                e.preventDefault();
                onNudge?.(e.key === "ArrowUp" ? -1 : 1);
                return;
            }
            if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                onRemove();
            }
        }, sx: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            px: 1,
            py: 0.875,
            mb: 0.75,
            borderRadius: "8px",
            border: `1px ${isDragging ? "dashed" : "solid"} ${isDragging ? "#FF008E" : "#EAECF0"}`,
            bgcolor: isDragging ? "#FFF5FA" : "#fff",
            cursor: draggable ? "grab" : "default",
            outline: "none",
            userSelect: "none",
            transition: "border-color .15s ease, background-color .15s ease, box-shadow .15s ease",
            "& .rb-row-content": { opacity: isDragging ? 0.45 : 1, transition: "opacity .15s ease" },
            "&:hover": draggable && !isDragging
                ? { borderColor: "#D0D5DD", boxShadow: "0 1px 3px rgba(16, 24, 40, 0.08)" }
                : undefined,
            "&:hover .rb-grip": { color: "#667085" },
            "&:active": draggable ? { cursor: "grabbing" } : undefined,
            "&:focus-visible": { boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
        }, children: [_jsxs(Box, { className: "rb-row-content", sx: { display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }, children: [draggable && (_jsx(Box, { className: "rb-grip", sx: { display: "flex", color: "#98A2B3", flex: "0 0 auto", transition: "color .15s ease" }, children: _jsx(GripVertical, { size: 15 }) })), _jsx(Typography, { component: "span", sx: {
                            minWidth: 20,
                            height: 20,
                            px: 0.5,
                            borderRadius: "6px",
                            bgcolor: "#F2F4F7",
                            color: "#475467",
                            fontSize: "11px !important",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                        }, children: index + 1 }), _jsx(Box, { sx: { display: "flex", color: networkColor(networkKey), flex: "0 0 auto" }, children: _jsx(GroupIcon, { group: networkKey, size: 16 }) }), _jsx(Typography, { className: "all-Font-Sizes", sx: {
                            flex: 1,
                            minWidth: 0,
                            color: "#101828",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }, title: label, children: label })] }), !disabled && (_jsx(HoverTooltip, { title: "Remove", placement: "top", children: _jsx(Box, { role: "button", "aria-label": `Remove ${label}`, tabIndex: 0, draggable: false, onMouseDown: (e) => e.stopPropagation(), onClick: onRemove, onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            onRemove();
                        }
                    }, sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        flex: "0 0 auto",
                        borderRadius: "6px",
                        cursor: "pointer",
                        color: "#98A2B3",
                        transition: "background-color .15s ease, color .15s ease",
                        "&:hover": { bgcolor: "#FEF3F2", color: "#D92D20" },
                        "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(217,45,32,0.25)" },
                    }, children: _jsx(Trash2, { size: 15 }) }) }))] }));
};
/**
 * FLIP animation for a reordered list: after each render, every row that moved
 * starts from where it was and glides to its new place, so a live drag reorder
 * reads as rows making room rather than jumping.
 */
/** How long a row takes to glide into its new place. */
const FLIP_MS = 180;
/**
 * `animate` is on only while a row is dragged: animating a removal made the
 * rows below start from their old, lower place and overflow the list for a
 * moment, flashing its scrollbar.
 */
const useFlip = (order, animate) => {
    const nodes = useRef(new Map());
    const lastTops = useRef(new Map());
    const wasAnimating = useRef(false);
    useLayoutEffect(() => {
        // The first pass of a drag only records where the rows are now. What was
        // recorded earlier can be stale (the pane was mounted mid open-animation,
        // or the list moved since), which made the first reorder jump.
        const dragJustStarted = animate && !wasAnimating.current;
        wasAnimating.current = animate;
        const nextTops = new Map();
        nodes.current.forEach((node, id) => {
            // offsetTop is the row's place in the list's layout - unlike the
            // viewport rect it is not skewed by the pane's open/close scale
            // animation, the page scroll, or a row's own glide in progress.
            const top = node.offsetTop;
            nextTops.set(id, top);
            const prev = lastTops.current.get(id);
            if (!animate || dragJustStarted || prev === undefined || prev === top)
                return;
            node.style.transition = "none";
            node.style.transform = `translateY(${prev - top}px)`;
            requestAnimationFrame(() => {
                node.style.transition = `transform ${FLIP_MS}ms ease`;
                node.style.transform = "";
            });
        });
        lastTops.current = nextTops;
    }, [order, animate]);
    return (id) => (el) => {
        if (el)
            nodes.current.set(id, el);
        else
            nodes.current.delete(id);
    };
};
/** A pick-one toggle: Klaviyo Campaign | Klaviyo Flow, Email | SMS. */
const SegmentedChoice = ({ options, value, onChange, disabled = false }) => (_jsx(Box, { role: "radiogroup", sx: { display: "flex", gap: 0.5, p: 0.375, bgcolor: "#F2F4F7", borderRadius: "8px" }, children: options.map((option) => {
        const on = option.value === value;
        return (_jsx(Box, { role: "radio", "aria-checked": on, "aria-disabled": disabled, tabIndex: disabled ? -1 : 0, onClick: () => !disabled && onChange(option.value), onKeyDown: (e) => {
                if (!disabled && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onChange(option.value);
                }
            }, sx: {
                flex: 1,
                textAlign: "center",
                py: 0.625,
                px: 1,
                borderRadius: "6px",
                cursor: disabled ? "not-allowed" : "pointer",
                bgcolor: on ? "#fff" : "transparent",
                boxShadow: on ? "0 1px 2px rgba(16, 24, 40, 0.08)" : "none",
                color: on ? "#C11574" : "#475467",
                fontSize: "12px !important",
                fontWeight: 600,
                whiteSpace: "nowrap",
                transition: "background-color .15s ease, color .15s ease, box-shadow .15s ease",
                "&:hover": disabled || on ? undefined : { color: "#101828" },
                "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
            }, children: option.label }, option.value));
    }) }));
/** "PERFORMANCE_MAX" → "Performance Max" for the checkbox labels; the value sent stays raw. */
const prettyEnum = (value) => value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
/** Case-insensitive "contains" — `query` is already lower-cased. */
const metricMatches = (label, query) => label.toLowerCase().includes(query);
/**
 * One network in the Networks section, as an accordion. The header carries the
 * network's icon, name and how many of its metrics are in the report; opening
 * it slides down every metric the network has, each tickable into the report's
 * columns (the Metrics list below).
 */
const NetworkAccordion = ({ network, open, onToggle, selectedIds, onToggleMetric, onSetMetrics, disabled = false, query = "", filters, onChangeFilters, showBreakdowns = false, breakdowns, dimensions, onChangeBreakdowns, enumFilters, onChangeEnumFilter, source, onChangeSource, }) => {
    const activeSource = sourceFor(network, source);
    // What the picked source carries — Klaviyo Flow has no Email / SMS channel.
    const filterFields = network.filterFields.filter((field) => !activeSource?.unsupportedFilterFields.includes(field.key));
    const enumFields = network.enumFields.filter((field) => !activeSource?.unsupportedEnumFields.includes(field.key));
    const hasFilterFields = filterFields.length > 0;
    const allMetrics = network.metrics.filter((metric) => isMetricAvailable(metric, network, dimensions, breakdowns, activeSource));
    const metrics = query ? allMetrics.filter((metric) => metricMatches(metric.label, query)) : allMetrics;
    // The badge counts the whole network, not just what the search shows.
    const pickedCount = allMetrics.filter((metric) => selectedIds.includes(metric.id)).length;
    // "Select all" acts on the metrics listed (a search narrows it).
    const listedPicked = metrics.filter((metric) => selectedIds.includes(metric.id)).length;
    const allListedPicked = metrics.length > 0 && listedPicked === metrics.length;
    const someListedPicked = listedPicked > 0 && !allListedPicked;
    const toggleAllListed = () => {
        if (disabled)
            return;
        onSetMetrics(metrics.map((metric) => metric.id), !allListedPicked);
    };
    const color = networkColor(network.key);
    return (_jsxs(Box, { sx: {
            border: `1px solid ${open ? "#D0D5DD" : "#EAECF0"}`,
            borderRadius: "8px",
            bgcolor: "#fff",
            overflow: "hidden",
            boxShadow: open ? "0 1px 3px rgba(16, 24, 40, 0.06)" : "none",
            transition: "border-color .2s ease, box-shadow .2s ease",
        }, children: [_jsxs(Box, { role: "button", tabIndex: 0, "aria-expanded": open, "aria-label": `${network.label} metrics`, onClick: onToggle, onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onToggle();
                    }
                }, sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 1,
                    cursor: "pointer",
                    bgcolor: open ? "#FCFCFD" : "transparent",
                    transition: "background-color .2s ease",
                    "&:hover": { bgcolor: "#F9FAFB" },
                    "&:focus-visible": { outline: "none", boxShadow: "inset 0 0 0 2px rgba(255,0,142,0.25)" },
                }, children: [_jsx(Box, { sx: { display: "flex", color }, children: _jsx(GroupIcon, { group: network.key, size: 20 }) }), _jsx(Typography, { className: "all-Font-Sizes", sx: { flex: 1, minWidth: 0, color: "#101828", fontWeight: 600 }, children: network.label }), _jsxs(Typography, { className: "all-Font-Sizes-12", sx: {
                            px: 0.75,
                            py: 0.125,
                            borderRadius: "10px",
                            bgcolor: pickedCount ? "#FDF2F8" : "#F2F4F7",
                            color: pickedCount ? "#C11574" : "#667085",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            transition: "background-color .2s ease, color .2s ease",
                        }, children: [pickedCount, "/", allMetrics.length] }), _jsx(Box, { sx: {
                            display: "flex",
                            color: open ? "#344054" : "#98A2B3",
                            transform: open ? "rotate(180deg)" : "none",
                            transition: "transform .25s ease, color .2s ease",
                        }, children: _jsx(ChevronDown, { size: 16 }) })] }), _jsxs(Collapse, { in: open, timeout: 250, unmountOnExit: true, children: [network.sourceChoices.length > 1 && !query && (_jsxs(Box, { sx: { borderBottom: "1px solid #F2F4F7", bgcolor: "#FCFCFD", px: 1.25, py: 1 }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#475467", fontWeight: 600, mb: 0.75 }, children: "Source" }), _jsx(SegmentedChoice, { options: network.sourceChoices.map((choice) => ({ value: choice.key, label: choice.label })), value: activeSource?.key || "", onChange: onChangeSource, disabled: disabled })] })), enumFields.length > 0 && !query && (_jsx(Box, { sx: {
                            borderBottom: "1px solid #F2F4F7",
                            bgcolor: "#FCFCFD",
                            px: 1.25,
                            py: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }, children: enumFields.map((field) => (_jsxs(Box, { children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#475467", fontWeight: 600, mb: 0.75 }, children: field.label }), field.singleSelect ? (
                                // Pick one, always one — the first value (Email) until changed.
                                _jsx(SegmentedChoice, { options: field.values.map((value) => ({ value, label: prettyEnum(value) })), value: (enumFilters[field.key] || [])[0] || field.values[0] || "", onChange: (value) => onChangeEnumFilter(field.key, [value]), disabled: disabled })) : (
                                /* Same picker as Breakdown Dimension — chips + checkbox popover. */
                                _jsx(BreakdownDimensionPicker, { value: enumFilters[field.key] || [], onChange: (values) => onChangeEnumFilter(field.key, values), options: field.values.map((value) => ({ value, label: prettyEnum(value) })), heading: field.label, placeholder: "All " + field.label.toLowerCase() + "s", disabled: disabled }))] }, field.key))) })), showBreakdowns && network.breakdownFields.length > 0 && !query && (_jsxs(Box, { sx: { borderBottom: "1px solid #F2F4F7", bgcolor: "#FCFCFD", px: 1.25, py: 1 }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#475467", fontWeight: 500, mb: 0.75 }, children: "Additional Breakdown" }), _jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.75 }, children: network.breakdownFields.map((field) => {
                                    // Campaign Name is the Campaign column itself: always ticked,
                                    // never a choice (and it adds no column of its own).
                                    const locked = field.key === "campaign_name";
                                    const on = locked || breakdowns.includes(field.key);
                                    const toggle = () => {
                                        if (disabled || locked)
                                            return;
                                        const next = on
                                            ? breakdowns.filter((k) => k !== field.key)
                                            : [...breakdowns, field.key];
                                        // Keep the API's coarsest → finest order.
                                        onChangeBreakdowns(network.breakdownFields.map((f) => f.key).filter((k) => next.includes(k)));
                                    };
                                    return (_jsxs(Box, { role: "checkbox", "aria-checked": on, "aria-disabled": disabled || locked, tabIndex: disabled || locked ? -1 : 0, onClick: toggle, onKeyDown: (e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                toggle();
                                            }
                                        }, sx: {
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            minHeight: 28,
                                            px: 1,
                                            borderRadius: "6px",
                                            border: `1px solid ${on ? "#FF008E" : "#D0D5DD"}`,
                                            bgcolor: on ? "#FFF5FA" : "#fff",
                                            cursor: disabled ? "not-allowed" : locked ? "default" : "pointer",
                                            transition: "border-color .15s ease, background-color .15s ease",
                                            "&:hover": disabled || locked ? undefined : { borderColor: on ? "#E6007A" : "#98A2B3" },
                                            "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
                                        }, children: [_jsx(Box, { sx: {
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: "4px",
                                                    border: `1.5px solid ${on ? "#FF008E" : "#98A2B3"}`,
                                                    bgcolor: on ? "#FF008E" : "#fff",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }, children: on && _jsx(Check, { size: 10, strokeWidth: 3 }) }), _jsx(Typography, { component: "span", sx: { color: on ? "#C11574" : "#344054", fontWeight: on ? 600 : 500, fontSize: "13px" }, children: field.label })] }, field.key));
                                }) })] })), hasFilterFields && !query && (_jsx(Box, { sx: { borderTop: "1px solid #F2F4F7", borderBottom: "1px solid #F2F4F7", bgcolor: "#FCFCFD" }, children: _jsx(NetworkFilters, { fields: filterFields, operators: network.operators, filters: filters, onChange: onChangeFilters, disabled: disabled }) })), _jsx(Box, { sx: {
                            borderTop: "1px solid #F2F4F7",
                            py: 0.5,
                            maxHeight: LIST_MAX_HEIGHT,
                            overflowY: "auto",
                        }, children: metrics.length === 0 ? (_jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#667085", px: 1.25, py: 0.75 }, children: query ? "No matching metrics." : "No metrics for this network yet." })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { role: "checkbox", "aria-checked": someListedPicked ? "mixed" : allListedPicked, "aria-disabled": disabled, tabIndex: disabled ? -1 : 0, onClick: toggleAllListed, onKeyDown: (e) => {
                                        if (!disabled && (e.key === "Enter" || e.key === " ")) {
                                            e.preventDefault();
                                            toggleAllListed();
                                        }
                                    }, sx: {
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1.25,
                                        py: 0.75,
                                        mb: 0.25,
                                        borderBottom: "1px solid #F2F4F7",
                                        cursor: disabled ? "not-allowed" : "pointer",
                                        transition: "background-color .15s ease",
                                        "&:hover": disabled ? undefined : { bgcolor: "#F9FAFB" },
                                        "&:focus-visible": {
                                            outline: "none",
                                            boxShadow: "inset 0 0 0 2px rgba(255,0,142,0.25)",
                                        },
                                    }, children: [_jsxs(Box, { sx: {
                                                width: 16,
                                                height: 16,
                                                flex: "0 0 auto",
                                                borderRadius: "4px",
                                                border: `1.5px solid ${allListedPicked || someListedPicked ? "#FF008E" : "#98A2B3"}`,
                                                bgcolor: allListedPicked || someListedPicked ? "#FF008E" : "#fff",
                                                color: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "background-color .15s ease, border-color .15s ease",
                                            }, children: [allListedPicked && _jsx(Check, { size: 11, strokeWidth: 3 }), someListedPicked && _jsx(Box, { sx: { width: 8, height: 2, borderRadius: "1px", bgcolor: "#fff" } })] }), _jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#101828", fontWeight: 600 }, children: "Select all" })] }), metrics.map((metric) => {
                                    const checked = selectedIds.includes(metric.id);
                                    return (_jsxs(Box, { role: "checkbox", "aria-checked": checked, "aria-disabled": disabled, tabIndex: disabled ? -1 : 0, title: metric.columnLabel, onClick: () => !disabled && onToggleMetric(metric.id), onKeyDown: (e) => {
                                            if (!disabled && (e.key === "Enter" || e.key === " ")) {
                                                e.preventDefault();
                                                onToggleMetric(metric.id);
                                            }
                                        }, sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            px: 1.25,
                                            py: 0.75,
                                            cursor: disabled ? "not-allowed" : "pointer",
                                            transition: "background-color .15s ease",
                                            "&:hover": disabled ? undefined : { bgcolor: "#F9FAFB" },
                                            "&:focus-visible": {
                                                outline: "none",
                                                boxShadow: "inset 0 0 0 2px rgba(255,0,142,0.25)",
                                            },
                                        }, children: [_jsx(Box, { sx: {
                                                    width: 16,
                                                    height: 16,
                                                    flex: "0 0 auto",
                                                    borderRadius: "4px",
                                                    border: `1.5px solid ${checked ? "#FF008E" : "#98A2B3"}`,
                                                    bgcolor: checked ? "#FF008E" : "#fff",
                                                    color: "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    transition: "background-color .15s ease, border-color .15s ease",
                                                }, children: checked && _jsx(Check, { size: 11, strokeWidth: 3 }) }), _jsx(Typography, { className: "all-Font-Sizes", sx: { color: checked ? "#101828" : "#344054", fontWeight: checked ? 500 : 400 }, children: metric.label })] }, metric.id));
                                })] })) })] })] }));
};
const BuilderControlsPanel = ({ state, disabled = false, onApplyMetrics, onRemoveMetric, onReorderColumns, breakdownDimensions, onChangeBreakdown, availableNetworks, networksLoading = false, hasClient = false, onChangeFilters, onChangeBreakdowns, onChangeEnumFilter, onChangeSource, onChangeCustomMetrics, onSaveCustomMetric, onDeleteCustomMetric, customEditing: controlledEditing, onCustomEditingChange, onGenerateAndSave, onClearAll, generateDisabledReason, generating = false, saving = false, hasSelection = false, hasGeneratedOnce = false, onClone, cloning = false, cloneDisabledReason, allowCustomMetrics = true, hideControlsTab, }) => {
    const [expanded, setExpanded] = useState('breakdown');
    // ── Column reorder (drag and drop) ──────────────────────────────────
    /** Column being dragged, and the live order while it is in the air. */
    const [draggingId, setDraggingId] = useState(null);
    const [dragOrder, setDragOrder] = useState(null);
    const displayOrder = dragOrder ?? state.column_order;
    const flipRef = useFlip(displayOrder, draggingId !== null);
    /** The scrollable column list, for edge auto-scroll while dragging. */
    const listRef = useRef(null);
    const scrollTimerRef = useRef(null);
    const scrollDirRef = useRef(0);
    const stopAutoScroll = useCallback(() => {
        if (scrollTimerRef.current !== null) {
            window.clearInterval(scrollTimerRef.current);
            scrollTimerRef.current = null;
        }
        scrollDirRef.current = 0;
    }, []);
    /**
     * Scroll the list while a row is dragged near its top or bottom edge.
     * A timer (rather than reacting to each `dragover`) so it keeps scrolling
     * when the pointer is held still at the edge.
     */
    const edgeAutoScroll = useCallback((clientY) => {
        const el = listRef.current;
        if (!el)
            return;
        const { top, bottom } = el.getBoundingClientRect();
        const direction = clientY < top + AUTO_SCROLL_EDGE_PX
            ? -1
            : clientY > bottom - AUTO_SCROLL_EDGE_PX
                ? 1
                : 0;
        scrollDirRef.current = direction;
        if (direction === 0) {
            stopAutoScroll();
            return;
        }
        if (scrollTimerRef.current !== null)
            return;
        scrollTimerRef.current = window.setInterval(() => {
            const node = listRef.current;
            if (!node || scrollDirRef.current === 0) {
                stopAutoScroll();
                return;
            }
            node.scrollTop += scrollDirRef.current * AUTO_SCROLL_STEP_PX;
        }, 16);
    }, [stopAutoScroll]);
    // A drag can end anywhere (drop, Escape, unmount) — never leave it running.
    useEffect(() => stopAutoScroll, [stopAutoScroll]);
    const startDrag = (id, e) => {
        e.dataTransfer.effectAllowed = "move";
        setDraggingId(id);
        setDragOrder(state.column_order);
    };
    /** The row most recently swapped past, and when — it is still gliding. */
    const lastSwap = useRef(null);
    /**
     * Over another row, move the dragged one into its slot — but only once the
     * pointer is past that row's middle in the direction of travel, and not
     * while that row is still gliding from the last swap. Swapping on mere
     * entry made the rows flip back and forth under the pointer.
     */
    const dragOver = (targetId, e) => {
        if (!draggingId || targetId === draggingId)
            return;
        const order = dragOrder ?? state.column_order;
        const from = order.indexOf(draggingId);
        const to = order.indexOf(targetId);
        if (from < 0 || to < 0)
            return;
        const last = lastSwap.current;
        if (last && last.id === targetId && Date.now() - last.at < FLIP_MS + 40)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const middle = rect.top + rect.height / 2;
        if (from < to && e.clientY < middle)
            return;
        if (from > to && e.clientY > middle)
            return;
        lastSwap.current = { id: targetId, at: Date.now() };
        setDragOrder(moveItem(order, from, to));
    };
    const endDrag = () => {
        if (dragOrder && dragOrder.join() !== state.column_order.join())
            onReorderColumns(dragOrder);
        setDraggingId(null);
        setDragOrder(null);
        lastSwap.current = null;
        stopAutoScroll();
    };
    const hasNetwork = state.network_keys.length > 0;
    const hasColumns = state.column_order.length > 0;
    // ── Selected Metrics pane ────────────────────────────────────────────
    /** Hidden with its ×; re-opens by itself whenever a metric is added. */
    const [selectedOpen, setSelectedOpen] = useState(true);
    const columnCount = state.column_order.length;
    const prevColumnCount = useRef(columnCount);
    useEffect(() => {
        if (columnCount > prevColumnCount.current)
            setSelectedOpen(true);
        prevColumnCount.current = columnCount;
    }, [columnCount]);
    const showSelected = selectedOpen;
    const toggleSection = (key) => setExpanded((prev) => (prev === key ? null : key));
    const hasBreakdown = breakdownDimensions.length > 0;
    // ── Network accordions ───────────────────────────────────────────────
    /** The network whose metric list is open — one at a time; opening another closes it. */
    const [openNetworks, setOpenNetworks] = useState([]);
    /** Text of the metric search at the top of the section — kept here so it
     *  survives the section collapsing; `metricQuery` is its lower-cased form. */
    const [metricSearch, setMetricSearch] = useState("");
    const metricQuery = metricSearch.trim().toLowerCase();
    /** Networks to list — all of them, or only those with a matching metric. */
    /** Every offered metric by column id — resolves the Selected Metrics list. */
    const metricsById = new Map(availableNetworks.flatMap((network) => network.metrics.map((metric) => [metric.id, metric])));
    // ── Custom metrics ────────────────────────────────────────────────────
    const customMetrics = state.custom_metrics || [];
    /** null = closed; "new" = create; a key = editing that metric. */
    const [localEditing, setLocalEditing] = useState(null);
    const customEditing = controlledEditing !== undefined ? controlledEditing : localEditing;
    const setCustomEditing = (value) => onCustomEditingChange ? onCustomEditingChange(value) : setLocalEditing(value);
    const [customSaving, setCustomSaving] = useState(false);
    const editingMetric = customMetrics.find((m) => m.key === customEditing) || null;
    /** Header text for a report column key ("meta.spend" or a custom key). */
    const columnHeader = (apiKey) => {
        const custom = customMetrics.find((m) => m.key === apiKey);
        if (custom)
            return custom.name;
        const id = state.column_order.find((colId) => toApiColumnKey(colId) === apiKey);
        return (id && metricsById.get(id)?.columnLabel) || apiKey;
    };
    /**
     * What a formula can read: the report's numeric columns (an Account ID is
     * not a number) and custom metrics defined before the one being edited.
     */
    const formulaMetrics = (() => {
        const columns = state.column_order
            .map((id) => metricsById.get(id))
            .filter((m) => Boolean(m) && m.kind !== "identifier")
            .map((m) => ({ key: toApiColumnKey(m.id), label: m.columnLabel, group: m.networkLabel, networkKey: m.networkKey }));
        const editIndex = editingMetric ? customMetrics.indexOf(editingMetric) : customMetrics.length;
        const earlier = customMetrics
            .slice(0, editIndex)
            .map((m) => ({ key: m.key, label: m.name, group: "Custom metrics" }));
        return [...columns, ...earlier];
    })();
    /** Column position choices: every column, then the other custom metrics. */
    const positionOptions = [
        ...state.column_order.map((id) => ({ value: toApiColumnKey(id), label: columnHeader(toApiColumnKey(id)) })),
        ...customMetrics.filter((m) => m.key !== customEditing).map((m) => ({ value: m.key, label: m.name })),
    ];
    const saveCustomMetric = async (metric) => {
        if (onSaveCustomMetric) {
            setCustomSaving(true);
            try {
                // A new metric goes without a key — the API names it.
                const done = await onSaveCustomMetric(editingMetric ? metric : { ...metric, key: "" }, Boolean(editingMetric));
                if (done)
                    setCustomEditing(null);
            }
            finally {
                setCustomSaving(false);
            }
            return;
        }
        onChangeCustomMetrics(editingMetric
            ? customMetrics.map((m) => (m.key === metric.key ? metric : m))
            : [...customMetrics, metric]);
        setCustomEditing(null);
    };
    /** Removing one also removes any later metric whose formula used it. */
    /** The metric whose bin was clicked — removed only once confirmed. */
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const confirmRemoveCustomMetric = async () => {
        if (!confirmRemove)
            return;
        setRemoving(true);
        try {
            await removeCustomMetric(confirmRemove.key);
        }
        finally {
            setRemoving(false);
            setConfirmRemove(null);
        }
    };
    const removeCustomMetric = async (key) => {
        // The API refuses while another metric depends on it, and says which.
        if (onDeleteCustomMetric) {
            await onDeleteCustomMetric(key);
            return;
        }
        const gone = new Set([key]);
        customMetrics.forEach((m) => {
            if (Array.from(gone).some((k) => m.formula.includes(`{${k}}`)))
                gone.add(m.key);
        });
        onChangeCustomMetrics(customMetrics.filter((m) => !gone.has(m.key)));
    };
    const searchedNetworks = metricQuery
        ? availableNetworks.filter((network) => network.metrics.some((metric) => metricMatches(metric.label, metricQuery)))
        : availableNetworks;
    const toggleNetwork = (key) => setOpenNetworks((prev) => prev.includes(key) ? [] : [key]);
    /** Tick adds the metric at the end of the column order; untick removes it. */
    const toggleMetric = (id) => state.column_order.includes(id)
        ? onRemoveMetric(id)
        : onApplyMetrics([...state.column_order, id]);
    /** "Select all" on a network: add every listed metric (after the ones
     *  already picked), or drop them all - in one change. */
    const setMetrics = (ids, on) => onApplyMetrics(on
        ? [...state.column_order, ...ids.filter((id) => !state.column_order.includes(id))]
        : state.column_order.filter((id) => !ids.includes(id)));
    return (_jsxs(Box, { sx: {
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 1,
        }, children: [_jsxs(Box, { sx: {
                    position: { xs: "static", lg: "absolute" },
                    top: 0,
                    bottom: 0,
                    right: { lg: `calc(100% + 16px)` },
                    zIndex: 10,
                    width: { xs: "100%", lg: RAIL_WIDTH },
                    pointerEvents: { lg: "none" },
                }, children: [hideControlsTab && showSelected && (_jsx(Box, { sx: { display: { xs: "none", lg: "block" }, position: "absolute", top: 12, left: -32, pointerEvents: "auto", zIndex: 11 }, children: hideControlsTab })), _jsx(Grow, { in: showSelected, timeout: 200, unmountOnExit: true, style: { transformOrigin: "right top" }, children: _jsxs(Box, { sx: {
                                position: { lg: "sticky" },
                                top: { lg: 8 },
                                pointerEvents: "auto",
                                bgcolor: "#fff",
                                border: "1px solid #EAECF0",
                                borderRadius: "12px",
                                overflow: "hidden",
                                boxShadow: "0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)",
                            }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25 }, children: [_jsxs(Typography, { className: "all-Font-Sizes-16", sx: { flex: 1, fontWeight: 600, color: "#101828" }, children: ["Selected Metrics (", state.column_order.length, ")"] }), _jsx(HoverTooltip, { title: "Hide selected metrics", placement: "top", children: _jsx(Box, { role: "button", "aria-label": "Hide selected metrics", tabIndex: 0, onClick: () => setSelectedOpen(false), onKeyDown: (e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        setSelectedOpen(false);
                                                    }
                                                }, sx: {
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: 26,
                                                    height: 26,
                                                    borderRadius: "6px",
                                                    color: "#667085",
                                                    cursor: "pointer",
                                                    transition: "background-color .15s ease, color .15s ease",
                                                    "&:hover": { bgcolor: "#F2F4F7", color: "#101828" },
                                                    "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
                                                }, children: _jsx(MinusIcon, { size: 16 }) }) })] }), _jsx(Box, { sx: { px: 1.5, pb: 1.5, borderTop: "1px solid #F2F4F7", pt: 1.25 }, children: !hasColumns ? (_jsxs(Box, { sx: {
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            textAlign: "center",
                                            gap: 0.75,
                                            py: 3,
                                            px: 1,
                                        }, children: [_jsx(Box, { sx: {
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: "50%",
                                                    bgcolor: "#F2F4F7",
                                                    color: "#667085",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    mb: 0.5,
                                                }, children: _jsx(ListChecks, { size: 18 }) }), _jsx(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 600, color: "#344054" }, children: "No metrics selected" }), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#667085", maxWidth: 240 }, children: disabled
                                                    ? "This report has no columns."
                                                    : "Open a network on the right and tick the metrics this report should show." })] })) : (_jsx(_Fragment, { children: _jsx(Box, { ref: listRef, onDragOver: (e) => {
                                                e.preventDefault();
                                                edgeAutoScroll(e.clientY);
                                            }, onDragLeave: stopAutoScroll, sx: {
                                                maxHeight: SELECTED_LIST_MAX_HEIGHT,
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                            }, children: displayOrder.map((id, index) => {
                                                const metric = metricsById.get(id);
                                                if (!metric)
                                                    return null;
                                                return (_jsx(SelectedRow, { rowRef: flipRef(id), index: index, label: metric.columnLabel, networkKey: metric.networkKey, disabled: disabled, onRemove: () => onRemoveMetric(id), isDragging: draggingId === id, onDragStart: (e) => startDrag(id, e), onDragOverRow: (e) => dragOver(id, e), onDropRow: endDrag, onDragEnd: endDrag, onNudge: (direction) => onReorderColumns(moveItem(state.column_order, index, index + direction)) }, id));
                                            }) }) })) }), customMetrics.length > 0 && (_jsxs(Box, { sx: { px: 1.5, pb: 1 }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#475467", fontWeight: 600, mb: 0.75 }, children: "Custom Metrics" }), customMetrics.map((metric) => (_jsxs(Box, { role: "button", tabIndex: disabled ? -1 : 0, title: metric.description || metric.formula, onClick: () => !disabled && setCustomEditing(metric.key), onKeyDown: (e) => {
                                                if (!disabled && (e.key === "Enter" || e.key === " ")) {
                                                    e.preventDefault();
                                                    setCustomEditing(metric.key);
                                                }
                                            }, sx: {
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                px: 1,
                                                py: 0.875,
                                                mb: 0.75,
                                                borderRadius: "8px",
                                                border: "1px solid #FCCEEE",
                                                bgcolor: "#FFFAFD",
                                                cursor: disabled ? "default" : "pointer",
                                                transition: "border-color .15s ease",
                                                "&:hover": disabled ? undefined : { borderColor: "#F670C7" },
                                                "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
                                            }, children: [_jsx(Typography, { component: "span", sx: { fontSize: "13px !important", fontWeight: 700, fontStyle: "italic", color: "#C11574", width: 20, textAlign: "center" }, children: "\u0192" }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#101828", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: metric.name }), _jsxs(Typography, { className: "all-Font-Sizes-12", sx: { color: "#667085", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: ["After \u201C", columnHeader(metric.position), "\u201D"] })] }), !disabled && (_jsx(HoverTooltip, { title: "Remove", placement: "top", children: _jsx(Box, { role: "button", "aria-label": `Remove ${metric.name}`, tabIndex: 0, onClick: (e) => {
                                                            e.stopPropagation();
                                                            setConfirmRemove(metric);
                                                        }, onKeyDown: (e) => {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setConfirmRemove(metric);
                                                            }
                                                        }, sx: {
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: "6px",
                                                            color: "#98A2B3",
                                                            transition: "background-color .15s ease, color .15s ease",
                                                            "&:hover": { bgcolor: "#FEF3F2", color: "#D92D20" },
                                                        }, children: _jsx(Trash2, { size: 15 }) }) }))] }, metric.key)))] })), !disabled && allowCustomMetrics && (_jsx(Box, { sx: { px: 1.5, pb: 1.5, pt: 0.5 }, children: _jsxs(Box, { component: "button", type: "button", onClick: () => setCustomEditing("new"), sx: {
                                            width: "100%",
                                            height: 40,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                            borderRadius: "8px",
                                            border: "1px dashed #F670C7",
                                            bgcolor: "#FFF5FA",
                                            color: "#C0006B",
                                            fontFamily: "inherit",
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            transition: "background-color .15s ease, border-color .15s ease",
                                            "&:hover": { bgcolor: "#FFEAF5", borderColor: "#FF008E" },
                                            "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
                                        }, children: [_jsx(Plus, { size: 16 }), "Create Custom Metric"] }) }))] }) }), _jsx(ConfirmActionModal, { open: Boolean(confirmRemove), title: "Delete custom metric", description: `Are you sure you want to delete "${confirmRemove?.name || ""}"? Its column will be removed from the report.`, confirmText: "Delete", isDeleteIcon: true, loading: removing, onConfirm: confirmRemoveCustomMetric, onClose: () => !removing && setConfirmRemove(null) }), _jsx(CustomMetricDialog, { open: customEditing !== null, onClose: () => setCustomEditing(null), onSave: saveCustomMetric, metrics: formulaMetrics, positions: positionOptions, takenNames: [
                            ...state.column_order.map((id) => metricsById.get(id)?.columnLabel || ""),
                            ...customMetrics.filter((m) => m.key !== customEditing).map((m) => m.name),
                        ].filter(Boolean), takenKeys: customMetrics.map((m) => m.key), initial: editingMetric, saving: customSaving, breakdownLabel: breakdownDimensions
                            .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
                            .join(", ") })] }), hideControlsTab && !showSelected && (_jsx(Box, { sx: { display: { xs: "none", lg: "block" }, position: "absolute", top: 3, left: -41, zIndex: 11 }, children: hideControlsTab })), _jsxs(Box, { sx: {
                    width: { xs: "100%", lg: RAIL_WIDTH },
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }, children: [!selectedOpen && (_jsxs(Box, { role: "button", tabIndex: 0, onClick: () => setSelectedOpen(true), onKeyDown: (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedOpen(true);
                            }
                        }, sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 1,
                            bgcolor: "#FFFAFD",
                            border: "1px solid #FFC8E9",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "background-color .2s ease, border-color .2s ease",
                            "&:hover": { bgcolor: "#FFF3FA", borderColor: "#FF008E" },
                            "&:focus-visible": { outline: "none", boxShadow: "0 0 0 2px rgba(255,0,142,0.25)" },
                        }, children: [_jsx(Typography, { className: "all-Font-Sizes", sx: { flex: 1, fontWeight: 600, color: "#C11574" }, children: "Selected Metrics" }), _jsxs(Typography, { className: "all-Font-Sizes-12", sx: { color: "#C11574" }, children: [state.column_order.length, " \u00B7 Show"] })] })), _jsx(Section, { label: "Breakdown Dimension", caption: hasBreakdown ? `${breakdownDimensions.length} selected` : "None", locked: disabled, expanded: expanded === "breakdown", onToggle: () => toggleSection("breakdown"), children: _jsx(BreakdownDimensionPicker, { value: breakdownDimensions, onChange: onChangeBreakdown, disabled: disabled }) }), _jsx(Section, { label: "Networks", caption: hasNetwork ? `${state.network_keys.length} available` : "None", locked: !hasClient || !hasBreakdown, lockedHint: !hasClient ? "Select a client first" : "Pick a breakdown dimension first", expanded: expanded === "networks", onToggle: () => toggleSection("networks"), children: _jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.25 }, children: [networksLoading ? (_jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#667085" }, children: "Loading networks\u2026" })) : availableNetworks.length === 0 ? (_jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#667085" }, children: "No connected network supports this breakdown." })) : (
                                // One search across every network's metrics. While it has
                                // text, only networks with a match show, opened.
                                _jsx(SearchInput, { placeholder: "Search metrics", size: "sm", debounceMs: 150, value: metricSearch, onSearch: setMetricSearch })), !networksLoading && metricQuery && searchedNetworks.length === 0 && availableNetworks.length > 0 && (_jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#667085", textAlign: "center", py: 1 }, children: "No metrics match your search." })), !networksLoading && searchedNetworks.map((network) => {
                                    return (_jsx(Box, { children: _jsx(NetworkAccordion, { network: network, open: Boolean(metricQuery) || openNetworks.includes(network.key), onToggle: () => toggleNetwork(network.key), selectedIds: state.column_order, onToggleMetric: toggleMetric, onSetMetrics: setMetrics, disabled: disabled, query: metricQuery, filters: state.filters_by_network[network.key] || [], onChangeFilters: (filters) => onChangeFilters(network.key, filters), showBreakdowns: breakdownDimensions.includes("campaign"), dimensions: breakdownDimensions, enumFilters: state.enum_filters_by_network?.[network.key] || {}, onChangeEnumFilter: (fieldKey, values) => onChangeEnumFilter(network.key, fieldKey, values), source: state.source_by_network?.[network.key], onChangeSource: (key) => onChangeSource(network.key, key), breakdowns: state.breakdowns_by_network?.[network.key] || [], onChangeBreakdowns: (keys) => onChangeBreakdowns(network.key, keys) }) }, network.key));
                                })] }) }), !disabled && (_jsxs(Box, { sx: {
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 0.5,
                            // Stuck to the bottom of the screen while the rail is taller than
                            // it (open accordions), so Generate never scrolls out of reach.
                            // The negative margin + padding stretch it over the rail's own
                            // padding so nothing shows through beside it.
                            position: "sticky",
                            bottom: 0,
                            zIndex: 3,
                            mx: -1,
                            px: 1,
                            pt: 1,
                            pb: 1,
                            mb: -1,
                            bgcolor: "#F9FAFB",
                            borderTop: "1px solid #E4E7EC",
                            borderRadius: "0 0 8px 8px",
                        }, children: [_jsx(HoverTooltip, { title: generateDisabledReason || "", placement: "left", children: _jsx("span", { children: _jsx(AppButton, { fullWidth: true, variant: "contained", loading: generating || saving, disabled: Boolean(generateDisabledReason) || generating || saving || cloning, onClick: onGenerateAndSave, sx: {
                                            height: 40,
                                            px: 2,
                                            bgcolor: "#FF008E",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            whiteSpace: "nowrap",
                                            boxShadow: "none",
                                            "&:hover": { bgcolor: "#E6007A" },
                                            gap: 1
                                        }, children: hasGeneratedOnce ? "Regenerate Report" : "Generate Report" }) }) }), _jsx(HoverTooltip, { title: hasSelection ? "" : "Nothing to clear yet", placement: "left", children: _jsx("span", { style: { flex: 1 }, children: _jsx(AppButton, { fullWidth: true, variant: "outlined", disabled: !hasSelection || generating || saving || cloning, onClick: onClearAll, sx: {
                                            height: 40,
                                            px: 2,
                                            textTransform: "none",
                                            fontWeight: 600,
                                            borderColor: "#D0D5DD",
                                            color: "#344054",
                                            whiteSpace: "nowrap",
                                            "&:hover": { borderColor: "#98A2B3", bgcolor: "#F9FAFB" },
                                        }, children: "Clear All" }) }) }), onClone && (_jsx(HoverTooltip, { title: cloneDisabledReason || "Save a copy of this report", placement: "left", children: _jsx("span", { style: { flex: 1 }, children: _jsx(AppButton, { fullWidth: true, variant: "outlined", loading: cloning, disabled: Boolean(cloneDisabledReason) ||
                                            cloning ||
                                            generating ||
                                            saving, onClick: onClone, sx: {
                                            height: 40,
                                            px: 2,
                                            textTransform: "none",
                                            fontWeight: 600,
                                            borderColor: "#FFC8E9",
                                            color: "#FF008E",
                                            bgcolor: "#FFFAFD",
                                            whiteSpace: "nowrap",
                                            "&:hover": { borderColor: "#FF008E", bgcolor: "#FFF3FA" },
                                        }, children: "Clone Report" }) }) }))] }))] })] }));
};
export default BuilderControlsPanel;
